import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCellButton } from "src/modules/sm-base/models/table-cell-button.model";
import { TableCellEvent } from "src/modules/sm-base/models/table-cell-event.model";
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Dates } from "src/modules/utils/shared/dates";
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { ClassFieldTrip } from '../../shared/class-field-trip.entity';
import { ClassRegister } from '../../shared/class-register.entity';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';


@Component({
    selector: 'app-class-field-trips',
    templateUrl: './class-field-trips.component.html',
    styleUrls: ['./class-field-trips.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
  export class ClassFieldTripsComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { id: "number" };
    id: number;

    classRegister: ClassRegister;
    orgData: ClassFieldTrip[];
    table: TableData;
    isManager = false;

    async initParams(): Promise<boolean> {
        this.classRegister = await this.service.restGetClassRegister(this.id, ["fieldTrips"], null, true);
        let settings = await this.service.restGetClassRegisterSettings(this.classRegister.enaioSchoolClass.getYearNumber(), this.classRegister.isHalfYear);
        this.isManager = this.classRegister.enaioSchoolClass.isManager(this.app.getUserName(), settings);

        this.orgData = Objects.cloneDeep(this.classRegister.fieldTrips);

        this.table = new TableData([
            new TableColumn("fromDate", "Von", TableCellType.date, 1),
            new TableColumn("toDate", "Bis", TableCellType.date, 1),
            new TableColumn("fromHour", "Von Stunde", TableCellType.string, 10),
            new TableColumn("toHour", "Bis Stunde", TableCellType.string, 10),
            new TableColumn("title", "Bezeichnung", TableCellType.string, -1),
            new TableColumn("approved", "Freigabe", TableCellType.string, 10),
            new TableColumn("actions", "", TableCellType.button, 3)
        ], this.classRegister.fieldTrips.map(h => new TableRow("", h,
              {
                ... h,
                fromHour: h.fromHour == -1 ? "Ganzer Tag" : h.fromHour + 1,
                toHour: h.toHour == -1 ? "Ganzer Tag" : h.toHour + 1,
                approved: h.approvedDate != null ? h.approvedBy + " " + Dates.formatDefaultDate(h.approvedDate) : this.isManager ? "NEIN (Haken klicken für Freigabe)" : "NEIN",
                actions: [
                    new TableCellButton("approve", "fas fa-check", "Freigeben", this.isManager && h.approvedDate == null),
                    new TableCellButton("edit", "fas fa-pencil", "Bearbeiten"),
                    new TableCellButton("delete", "fas fa-trash", "Löschen")
                ]
            }
        )), ["from"]);

        await this.service.updateNavigationClassRegister(this.id, [
            { label: "Unterrichtsgänge", routerLink: ["/enaio-certificates", "class-registers", "field-trips", this.id] }
        ]);
        return true;
    }

    override getCanDeactivateMessage(): string {
        return this.isChanged() ? "" : null;
    }

    isChanged(): boolean {
        return !Objects.equalsDeep(this.fromGui(), this.orgData);
    }

    fromGui(): ClassFieldTrip[] {
        return this.table.rows.map(h => h.raw as ClassFieldTrip);
    }

    async save(): Promise<void> {
        await this.app.saveDataHandler(async () => RestEndpoint.main().query({ id: this.id }).body(this.fromGui()).post().run("api/cer/classregister/fieldTrips").getText(), this, true, true);
    }

    getForm(): FrontendForm {
        return new FrontendForm([
            new FrontendField("fromDate", "Von", FrontendFieldType.date, { 
                mandatory: true, 
                value: new Date(), 
                guiGroup: "fromDate", 
                guiWidthInGroup: 50 
            }),
            new FrontendField("fromHour", "Stunde", FrontendFieldType.dropdown, {
                mandatory: true, 
                value: -1, 
                guiGroup: "fromDate", 
                guiWidthInGroup: 50, 
                guiGroupOwnLabel: false,
                minValue: -1, 
                listItems: [new FrontendFieldListItem(-1, "Ganzer Tag"), ...Utils.getRange(0, EnaioCertificatesTools.getMaxHoursPerDay() - 1).map(hour => new FrontendFieldListItem(hour, "ab " + (hour + 1) + ". Stunde"))]
            }),
            new FrontendField("toDate", "Bis", FrontendFieldType.date, {
                mandatory: true, 
                value: new Date(), 
                guiGroup: "toDate", 
                guiWidthInGroup: 50,
                guiGroupOwnLabel: false
            }),
            new FrontendField("toHour", "Stunde", FrontendFieldType.dropdown, {
                mandatory: true, 
                value: -1, 
                guiGroup: "toDate", 
                guiWidthInGroup: 50, 
                guiGroupOwnLabel: false,
                minValue: -1, 
                listItems: [new FrontendFieldListItem(-1, "Ganzer Tag"), ...Utils.getRange(0, EnaioCertificatesTools.getMaxHoursPerDay() - 1).map(hour => new FrontendFieldListItem(hour, "bis " + (hour + 1) + ". Stunde"))]
            }),
            new FrontendField("title", "Bezeichnung", FrontendFieldType.string, { mandatory: true }),
            new FrontendField("type", "Art des Unterrichtsgangs", FrontendFieldType.dropdown, { 
                dropdownEditable: true,
                listItems: FrontendFieldListItem.make(["", "Wanderung", "Exkursion", "Theater-/Konzert-/Kinobesuch", "Museumsbesuch"], null, false) 
            }),
            new FrontendField("educationalProject", "Pädagogisches Vorhaben", FrontendFieldType.textArea),
            new FrontendField("transportation", "Transportmittel", FrontendFieldType.dropdown, { 
                dropdownEditable: true,
                listItems: FrontendFieldListItem.make(["", "ÖPNV", "Dienstfahrzeuge"], null, false) 
            }),
            new FrontendField("inClassPupilsSupervision", "Betreuung der in der Klasse verbleibenden Schüler", FrontendFieldType.textArea)
        ]);
    }

    async addFieldTrip(): Promise<void> {
        let fieldTrip = new ClassFieldTrip();
        let form = this.getForm();
        form.fill(fieldTrip);
        if (await this.app.dialog.form(form, "Unterrichtsgang anlegen") == "ok") {
            form.get(fieldTrip);
            let [ok, _] = await this.app.saveDataHandler(async () => RestEndpoint.main().post().query({id: this.classRegister.id}).body(fieldTrip).run("api/cer/classregister/fieldTrip").getText(), this, true, false);
            if (ok) {
                await this.initParams();
            }
        }
    }

    async fieldTripClicked(event: TableCellEvent): Promise<void> {
        if (event.isButton("edit", "actions")) {
            let fieldTrip = event.row.raw as ClassFieldTrip;
            let form = this.getForm();
            form.fill(fieldTrip);
            if (await this.app.dialog.form(form, "Unterrichtsgang bearbeiten") == "ok") {
                form.get(fieldTrip);
                let [ok, _] = await this.app.saveDataHandler(async () => RestEndpoint.main().put().query({id: this.classRegister.id}).body(fieldTrip).run("api/cer/classregister/fieldTrip").getText(), this, true, false);
                if (ok) {
                    await this.initParams();
                }
            }
        }
        else if (event.isButton("delete", "actions") &&
            await this.app.dialog.yesNo("Wirklich löschen?", "Warnung")) {
            if ((await this.app.deleteDataHandler(async () => RestEndpoint.main().delete().query({id: (event.row.raw as ClassFieldTrip).id}).run("api/cer/classregister/fieldTrip").getText()))[0]) {
                await this.initParams();
            }
        }
        else if (event.isButton("approve", "actions") &&
            await this.app.dialog.yesNo("Möchten Sie diesen Unterrichtsgang freigeben?", "Frage")) {
            let fieldTrip = event.row.raw as ClassFieldTrip;
            if ((await this.app.saveDataHandler(async () => RestEndpoint.main().post().query({id: fieldTrip.id}).body(fieldTrip).run("api/cer/classregister/fieldTripApprove").getText(), this, true, false))[0]) {
                await this.initParams();
            }
        }
    }
}
